import { useQueries, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type CapsDraft,
  CaseSettingsView,
  type ExtensionRow,
} from '@/components/case-overview/CaseSettingsView'
import { isCaptureImmutable } from '@/features/documents/capture-integrity'
import { extensionDescription, extensionName } from '@/features/extensions/extension-copy'
import { useCaseSettings, useResetCase, useUpdateCaseSettings } from '@/hooks/use-case-queries'
import { ADVANCED_FACE_DETECTION_EXTENSION_ID, EXTENSIONS_QUERY_KEY } from '@/hooks/use-extensions'
import { extensionsApi } from '@/services/api/extensions'

/**
 * Extensions that are on or off for the whole deployment, with no per-case
 * switch: Advanced Face Detection has none (ADR-0051).
 */
const DEPLOYMENT_WIDE_EXTENSIONS: ReadonlySet<string> = new Set([
  ADVANCED_FACE_DETECTION_EXTENSION_ID,
])

interface CaseSettingsProps {
  caseId: string
  onCaseReset?: () => void
}

/**
 * Connected case-settings page. Owns every query/mutation (settings, schemas,
 * extensions + per-extension health, reset) and the toasts; the pure
 * {@link CaseSettingsView} renders the form from the derived props.
 */
export function CaseSettings({ caseId, onCaseReset }: CaseSettingsProps) {
  const { t } = useTranslation()
  const { data: settings, isLoading: loadingSettings } = useCaseSettings(caseId)
  const updateSettings = useUpdateCaseSettings(caseId)
  const resetCase = useResetCase(caseId)

  const { data: extensions } = useQuery({
    queryKey: EXTENSIONS_QUERY_KEY,
    queryFn: () => extensionsApi.list(),
  })
  const globallyEnabled = (extensions ?? []).filter(
    e => e.enabled && !DEPLOYMENT_WIDE_EXTENSIONS.has(e.id)
  )

  // One health probe per globally-enabled extension. ``useQueries`` keeps the
  // count of hooks stable per render given a stable extension list.
  const healthQueries = useQueries({
    queries: globallyEnabled.map(ext => ({
      queryKey: ['extensions', ext.id, 'health'],
      queryFn: () => extensionsApi.health(ext.id),
      staleTime: 30_000,
    })),
  })

  const loading = loadingSettings
  const saving = updateSettings.isPending
  const resetting = resetCase.isPending

  const extensionRows: ExtensionRow[] = globallyEnabled.map((ext, i) => ({
    id: ext.id,
    name: extensionName(t, ext),
    description: extensionDescription(t, ext),
    // Default-on: a missing key for a globally-enabled extension means "on".
    checked: settings?.extensions?.[ext.id] ?? true,
    status: healthQueries[i]?.data?.status ?? 'unknown',
  }))

  function handleToggleExtension(id: string, next: boolean) {
    const map = { ...(settings?.extensions ?? {}) }
    map[id] = next
    updateSettings.mutate({ extensions: map })
  }

  function handleToggleInternalNodes(enabled: boolean) {
    updateSettings.mutate(
      { show_internal_nodes: enabled },
      {
        onSuccess: () =>
          toast.success(
            enabled
              ? t('cases.settings.toast.sourceNodesVisible')
              : t('cases.settings.toast.sourceNodesHidden')
          ),
      }
    )
  }

  function handleToggleStructuredIngest(enabled: boolean) {
    updateSettings.mutate(
      { show_structured_ingest: enabled },
      {
        onSuccess: () =>
          toast.success(
            enabled
              ? t('cases.settings.toast.structuredIngestShown')
              : t('cases.settings.toast.structuredIngestHidden')
          ),
      }
    )
  }

  function handleToggleCaseRoadmap(enabled: boolean) {
    updateSettings.mutate(
      { show_case_roadmap: enabled },
      {
        onSuccess: () =>
          toast.success(
            enabled
              ? t('cases.settings.toast.roadmapEnabled')
              : t('cases.settings.toast.roadmapHidden')
          ),
      }
    )
  }

  function handleResetCase() {
    resetCase.mutate(undefined, {
      onSuccess: result => {
        toast.success(
          t('cases.settings.toast.caseReset', {
            documents: result.documents_reset,
            entities: result.neo4j_nodes_deleted,
          })
        )
        onCaseReset?.()
      },
      onError: (err: unknown) => {
        // A case holding HUMINT captures is never reset: they are immutable evidence (ADR-0045).
        toast.error(
          isCaptureImmutable(err)
            ? t('documents.capture.resetRefused')
            : t('cases.settings.toast.resetFailed')
        )
      },
    })
  }

  function handleSaveCaps(caps: CapsDraft) {
    updateSettings.mutate(
      {
        hypothesis_run_step_cap: caps.step_cap,
        hypothesis_run_wall_clock_seconds: caps.wall_clock_seconds,
        hypothesis_run_max_tokens: caps.max_tokens,
        hypothesis_run_max_findings: caps.max_findings,
      },
      {
        onSuccess: () => toast.success(t('cases.settings.toast.capsUpdated')),
        onError: (err: unknown) => {
          const msg =
            err instanceof Error ? err.message : t('cases.settings.toast.validationFailed')
          toast.error(msg)
        },
      }
    )
  }

  function handleSaveAgentContext(context: string) {
    updateSettings.mutate(
      { additional_agent_context: context || null },
      {
        onSuccess: updated => {
          toast.success(
            updated.additional_agent_context
              ? t('cases.settings.toast.aiContextUpdated')
              : t('cases.settings.toast.aiContextCleared')
          )
        },
      }
    )
  }

  const savedCaps: CapsDraft = {
    step_cap: settings?.hypothesis_run_step_cap ?? 50,
    wall_clock_seconds: settings?.hypothesis_run_wall_clock_seconds ?? 600,
    max_tokens: settings?.hypothesis_run_max_tokens ?? 8192,
    max_findings: settings?.hypothesis_run_max_findings ?? 10,
  }

  return (
    <CaseSettingsView
      loading={loading}
      saving={saving}
      resetting={resetting}
      showCaseRoadmap={settings?.show_case_roadmap ?? true}
      onToggleCaseRoadmap={handleToggleCaseRoadmap}
      showStructuredIngest={settings?.show_structured_ingest ?? true}
      onToggleStructuredIngest={handleToggleStructuredIngest}
      extensions={extensionRows}
      onToggleExtension={handleToggleExtension}
      showInternalNodes={settings?.show_internal_nodes ?? false}
      onToggleInternalNodes={handleToggleInternalNodes}
      savedAgentContext={settings?.additional_agent_context ?? ''}
      onSaveAgentContext={handleSaveAgentContext}
      onResetCase={handleResetCase}
      savedCaps={savedCaps}
      onSaveCaps={handleSaveCaps}
    />
  )
}
